// MessagesScreen.tsx — v14
//
// Nouveautés vs v13 :
//   - PRÉSENCE POUSSÉE PAR LE BACKEND (aucun appel répété) :
//       • À la connexion de la socket de la liste, un seul événement
//         watch_presence abonne l'écran à la présence de tous les amis.
//       • Le backend pousse ensuite presence_update { user_id, is_online,
//         expires_in_ms } : l'écran met à jour ses listes LOCALEMENT, sans
//         aucune requête HTTP (l'ancien écouteur presence-update, que le
//         backend n'émettait jamais, rechargeait les listes par HTTP).
//       • Règle des 60 s côté frontend : un ami "en ligne" le reste pendant
//         expires_in_ms (60 s au maximum), puis passe hors ligne s'il n'y a
//         pas de nouveau signal. Un seul setTimeout par ami en ligne.
//       • Au chargement des listes (vraie lecture HTTP), le temps restant
//         (presenceExpiresInMs) arme ces minuteurs.
//       • null (statut caché) → aucun point vert, aucun texte.
//
// Nouveautés vs v12 (v13) :
//   - PRÉSENCE : cet écran n'ÉCRIT plus jamais la présence.
//       • Suppression de la fonction updatePresence et de ses deux appels
//         (montage → true, démontage → false) : la présence est désormais
//         écrite uniquement par le signal du layout racine (route /revue,
//         limité à 1 signal par 60 s).
//       • L'écran ne fait que LIRE la présence renvoyée par le backend
//         (getConversations, getFriendsOnline, getAllFriends).
//   - isOnline peut maintenant valoir true, false ou null :
//       true  → en ligne (signal de moins de 60 s)
//       false → hors ligne
//       null  → statut caché (ou aucune présence enregistrée) : aucun
//               statut n'est affiché dans l'onglet Amis, et aucun point
//               vert n'apparaît sur l'avatar.
//
// Nouveautés vs v11 (conservées de la v12) :
//   - Intégration du cache centralisé de messages (hooks/useConversationsCache.ts) :
//       • getCached/setCached tenus dans ce composant, transmis à ChatBox via
//         initialMessages/initialHasMore (hydratation instantanée) et
//         onMessagesUpdate (mise à jour continue du cache).
//   - CORRECTIF BUG "conversation qui recharge à chaque retour" :
//       backToList() ne remet plus activeConversation à null. Elle repasse
//       seulement viewMode à 'tabs'. ChatBox reste donc MONTÉ en arrière-plan
//       (cache visuel via style { display: 'none' } sur son wrapper) au lieu
//       d'être démonté — son socket, son state interne (messages, scroll,
//       input en cours d'édition) restent intacts. Revenir sur la MÊME
//       conversation est donc instantané, sans nouveau join_conversation ni
//       rechargement DB.
//   - Une seule conversation "vivante" à la fois : ouvrir une conversation
//       DIFFÉRENTE remplace activeConversation (l'ancien ChatBox est alors
//       bien démonté par React, un nouveau est monté pour la nouvelle
//       conversation) — évite d'accumuler des sockets ouverts indéfiniment.
//       Le cache de messages, lui, survit à ce démontage (stocké dans
//       useConversationsCache, pas dans ChatBox), donc rouvrir une ancienne
//       conversation plus tard reste instantané via initialMessages.
//
// Le reste (sync overlay, tabs, recherche, ouverture auto depuis Market,
// UserProfileView) est inchangé par rapport à v11.
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs, endpoints, clés AsyncStorage, noms de socket et
// tous les console.log restent codés en dur, inchangés.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  Platform, RefreshControl, TextInput, Modal, Animated,
} from 'react-native';
import { Ionicons }       from '@expo/vector-icons';
import AsyncStorage       from '@react-native-async-storage/async-storage';
import * as Haptics       from 'expo-haptics';
import { io, Socket }     from 'socket.io-client';
import ChatBox            from '../../components/ChatBox';
import UserProfileView    from '../../components/UserProfileView';
import GroupsTab, { GroupItem, GroupsTabRef } from '../../components/GroupsTab';
// ✅ Correct (chemin actuel réel)
import { useTabBarVisibility } from '@/components/CustomTabBar';
import { useScrollAutoHide } from '../../hooks/useScrollAutoHide';
// ── Cache centralisé des conversations (v12) ──────────────────────────────
import { useConversationsCache, type Message } from '../../hooks/useConversationsCache';
// ── NOUVEAU : lecture des query params pour ouverture auto depuis Market ──
// PATCH V17 : useFocusEffect en plus de useLocalSearchParams — voir note
// en tête de fichier.
import { useLocalSearchParams, useFocusEffect } from 'expo-router';
import { useTranslation } from 'react-i18next';

const WS_BASE      = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const SESSION_KEY  = 'harmonia_session';
const TOKEN_MARGIN = 60;

type ViewMode = 'tabs' | 'chat';
type TabMode  = 'history' | 'online' | 'friends' | 'groups';

// ── Synchronisation ───────────────────────────────────────────────────────────

type SyncStep = 'idle' | 'checking' | 'refreshing' | 'syncing' | 'done' | 'error';

interface SyncState {
  visible: boolean;
  step:    SyncStep;
  message: string;
  error:   string | null;
}

// ── Types ─────────────────────────────────────────────────────────────────────

// v13 : isOnline = true (en ligne) | false (hors ligne) | null (statut caché)
interface User {
  id: string; nom: string; prenom: string;
  avatar_url: string | null; isOnline?: boolean | null;
  // v14 : temps restant (ms) avant expiration du "en ligne", à la lecture HTTP
  presenceExpiresInMs?: number;
}
interface Conversation {
  type: 'private' | 'group'; id: string;
  otherUser?: User; name?: string; description?: string; memberCount?: number;
  lastMessage: {
    content: string;
    type?: string;
    createdAt: string;
    isFromMe?: boolean;
    senderName?: string;
  } | null;
  unreadCount?: number; createdAt: string;
}

// ── Token helpers ─────────────────────────────────────────────────────────────

async function getValidToken(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session      = JSON.parse(raw);
    const accessToken  = session?.access_token  ?? null;
    const refreshToken = session?.refresh_token  ?? null;
    const expiresAt    = session?.expires_at     ?? 0;
    if (!accessToken || !refreshToken) return null;
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (expiresAt - nowSeconds > TOKEN_MARGIN) return accessToken;
    const res = await fetch(`${WS_BASE}/refresh-token`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const { access_token, refresh_token: newRefresh, expires_at } = await res.json();
    await AsyncStorage.setItem(SESSION_KEY, JSON.stringify({
      ...session, access_token, refresh_token: newRefresh ?? refreshToken, expires_at,
    }));
    return access_token;
  } catch { return null; }
}

async function handleNewTokenFromResponse(data: any): Promise<void> {
  if (!data?.new_token) return;
  try {
    const raw = await AsyncStorage.getItem(SESSION_KEY);
    if (!raw) return;
    await AsyncStorage.setItem(SESSION_KEY, JSON.stringify({ ...JSON.parse(raw), access_token: data.new_token }));
  } catch {}
}

async function api(action: string, userId: string, extra?: any): Promise<any> {
  const token = await getValidToken();
  if (!token) return { success: false, error: 'session_expired' };
  const res = await fetch(`${WS_BASE}/messages`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, user_id: userId, access_token: token, ...extra }),
  });
  const data = await res.json();
  await handleNewTokenFromResponse(data);
  return data;
}

// ── Composants utilitaires ────────────────────────────────────────────────────

function LoadingText({ t }: { t: (key: string) => string }) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: 40 }}>
      <Text style={{ fontSize: 14, color: '#999' }}>{t('messagesScreen.loadingData')}</Text>
    </View>
  );
}

function EmptyState({ icon, title, subtitle, children }: {
  icon: string; title: string; subtitle?: string; children?: React.ReactNode;
}) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: 60, paddingHorizontal: 20 }}>
      <Ionicons name={icon as any} size={60} color="#ccc" />
      <Text style={{ fontSize: 17, fontWeight: '700', color: '#888', marginTop: 15, textAlign: 'center' }}>{title}</Text>
      {subtitle && <Text style={{ fontSize: 14, color: '#aaa', marginTop: 6, textAlign: 'center' }}>{subtitle}</Text>}
      {children}
    </View>
  );
}

function getLastMessagePreview(lastMessage: Conversation['lastMessage'], t: (key: string) => string): string {
  if (!lastMessage) return t('messagesScreen.preview.noMessage');
  if (lastMessage.content?.trim()) return lastMessage.content.trim();
  switch (lastMessage.type) {
    case 'image': return t('messagesScreen.preview.photo');
    case 'video': return t('messagesScreen.preview.video');
    case 'audio': return t('messagesScreen.preview.audio');
    case 'file':  return t('messagesScreen.preview.file');
    default:      return t('messagesScreen.preview.noMessage');
  }
}

function SyncOverlay({ state, t }: { state: SyncState; t: (key: string) => string }) {
  const spinAnim  = useRef(new Animated.Value(0)).current;
  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.88)).current;
  const checkAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (state.visible) {
      Animated.parallel([
        Animated.timing(fadeAnim,  { toValue: 1, duration: 220, useNativeDriver: true }),
        Animated.spring(scaleAnim, { toValue: 1, friction: 6, tension: 80, useNativeDriver: true }),
      ]).start();
    } else {
      Animated.timing(fadeAnim, { toValue: 0, duration: 180, useNativeDriver: true }).start();
    }
  }, [state.visible]);

  useEffect(() => {
    if (['checking', 'refreshing', 'syncing'].includes(state.step)) {
      Animated.loop(
        Animated.timing(spinAnim, { toValue: 1, duration: 900, useNativeDriver: true })
      ).start();
    } else {
      spinAnim.stopAnimation();
      spinAnim.setValue(0);
    }
  }, [state.step]);

  useEffect(() => {
    if (state.step === 'done') {
      Animated.spring(checkAnim, { toValue: 1, friction: 5, tension: 100, useNativeDriver: true }).start();
    } else {
      checkAnim.setValue(0);
    }
  }, [state.step]);

  const spin = spinAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });

  const SYNC_MESSAGES: Record<SyncStep, string> = {
    idle:       '',
    checking:   t('messagesScreen.sync.steps.checking'),
    refreshing: t('messagesScreen.sync.steps.refreshing'),
    syncing:    t('messagesScreen.sync.steps.syncing'),
    done:       t('messagesScreen.sync.done'),
    error:      '',
  };

  const steps: { key: SyncStep; label: string }[] = [
    { key: 'checking',   label: t('messagesScreen.sync.steps.checking') },
    { key: 'refreshing', label: t('messagesScreen.sync.steps.refreshing') },
    { key: 'syncing',    label: t('messagesScreen.sync.steps.syncingData') },
  ];
  const activeIdx = steps.findIndex(s => s.key === state.step);

  if (!state.visible) return null;

  return (
    <Modal transparent animationType="none" visible={state.visible}>
      <Animated.View style={[SO.backdrop, { opacity: fadeAnim }]}>
        <Animated.View style={[SO.card, { transform: [{ scale: scaleAnim }] }]}>
          <View style={SO.iconWrap}>
            {state.step === 'error' ? (
              <View style={[SO.iconCircle, SO.iconCircleError]}>
                <Ionicons name="alert-circle-outline" size={32} color="#DC2626" />
              </View>
            ) : state.step === 'done' ? (
              <Animated.View style={[SO.iconCircle, SO.iconCircleDone, { transform: [{ scale: checkAnim }] }]}>
                <Ionicons name="checkmark" size={32} color="#10B981" />
              </Animated.View>
            ) : (
              <Animated.View style={[SO.iconCircle, { transform: [{ rotate: spin }] }]}>
                <Ionicons name="sync-outline" size={32} color="#8A2BE2" />
              </Animated.View>
            )}
          </View>
          <Text style={SO.title}>
            {state.step === 'error' ? t('messagesScreen.sync.titleError')
              : state.step === 'done' ? t('messagesScreen.sync.titleDone')
              : t('messagesScreen.sync.titleInProgress')}
          </Text>
          <Text style={SO.message}>
            {state.step === 'error' ? (state.error ?? t('messagesScreen.sync.genericError')) : SYNC_MESSAGES[state.step]}
          </Text>
          {!['error', 'done', 'idle'].includes(state.step) && (
            <View style={SO.steps}>
              {steps.map((s, i) => {
                const isDone    = i < activeIdx;
                const isActive  = i === activeIdx;
                const isPending = i > activeIdx;
                return (
                  <View key={s.key} style={SO.stepRow}>
                    <View style={[SO.stepDot, isDone && SO.stepDotDone, isActive && SO.stepDotActive, isPending && SO.stepDotPending]}>
                      {isDone
                        ? <Ionicons name="checkmark" size={11} color="#fff" />
                        : <Text style={SO.stepDotTxt}>{i + 1}</Text>
                      }
                    </View>
                    <Text style={[SO.stepLabel, isActive && SO.stepLabelActive, isDone && SO.stepLabelDone, isPending && SO.stepLabelPending]} numberOfLines={1}>
                      {s.label}
                    </Text>
                  </View>
                );
              })}
            </View>
          )}
          {state.step === 'error' && (
            <View style={SO.errorBox}>
              <Ionicons name="information-circle-outline" size={15} color="#DC2626" style={{ marginRight: 6 }} />
              <Text style={SO.errorBoxTxt}>{state.error}</Text>
            </View>
          )}
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}

export default function MessagesScreen() {
  const { t } = useTranslation();
  const { setTabBarHidden } = useTabBarVisibility();

  const params = useLocalSearchParams<{
    openConversationId?: string;
    otherUserId?: string;
    otherUserNom?: string;
    otherUserPrenom?: string;
  }>();

  const { handleScroll, handleDoubleTap } = useScrollAutoHide();

  // ── Cache centralisé des messages (v12) ───────────────────────────────────
  const { getCached, setCached } = useConversationsCache();

  const [viewMode,   setViewMode]   = useState<ViewMode>('tabs');
  const [activeTab,  setActiveTab]  = useState<TabMode>('history');
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userId,     setUserId]     = useState('');
  const [userName,   setUserName]   = useState('');
  const [userRole,   setUserRole]   = useState('user');
  const accessTokenRef = useRef('');

  const [conversations,  setConversations]  = useState<Conversation[]>([]);
  const [onlineFriends,  setOnlineFriends]  = useState<User[]>([]);
  const [allFriends,     setAllFriends]     = useState<User[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const listSocketRef = useRef<Socket | null>(null);

  // v14 — présence : un minuteur local par ami "en ligne" (aucun appel réseau)
  // et un cache des profils pour pouvoir ajouter un ami à l'onglet "En ligne"
  // quand le backend pousse sa présence.
  const presenceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const friendsCacheRef   = useRef<Map<string, User>>(new Map());

  const groupsTabRef = useRef<GroupsTabRef>(null);

  const [searchQuery,   setSearchQuery]   = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<TextInput>(null);

  // PATCH V19 : petite animation de rotation pour l'icône 🔄 au tap
  const syncIconSpin = useRef(new Animated.Value(0)).current;

  const [selectedUserId,       setSelectedUserId]       = useState<string | null>(null);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);

  const [syncState, setSyncState] = useState<SyncState>({
    visible: false, step: 'idle', message: '', error: null,
  });
  const isSyncing = useRef(false);

  const setSyncStep = (step: SyncStep, error: string | null = null) => {
    const messages: Record<SyncStep, string> = {
      idle:       '',
      checking:   t('messagesScreen.sync.steps.checking'),
      refreshing: t('messagesScreen.sync.steps.refreshing'),
      syncing:    t('messagesScreen.sync.steps.syncing'),
      done:       t('messagesScreen.sync.done'),
      error:      '',
    };
    setSyncState({ visible: true, step, message: messages[step], error });
  };

  // PATCH V19 : renommé handleLongPressSync → handleSync, déclenché par un
  // tap simple sur l'icône 🔄 (plus de long-press requis).
  const handleSync = async () => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);

    Animated.timing(syncIconSpin, { toValue: 1, duration: 600, useNativeDriver: true }).start(() => {
      syncIconSpin.setValue(0);
    });

    setSyncStep('checking');
    await delay(500);

    let raw: string | null = null;
    try { raw = await AsyncStorage.getItem(SESSION_KEY); } catch {}

    if (!raw) {
      setSyncStep('error', t('messagesScreen.sync.errors.pleaseReconnectData'));
      autoDismissSync(); isSyncing.current = false; return;
    }

    let session: any;
    try { session = JSON.parse(raw); } catch {
      setSyncStep('error', t('messagesScreen.sync.errors.pleaseReconnect'));
      autoDismissSync(); isSyncing.current = false; return;
    }

    const storedRefreshToken = session?.refresh_token ?? null;
    const storedAccessToken  = session?.access_token  ?? null;
    if (!storedAccessToken || !storedRefreshToken) {
      setSyncStep('error', t('messagesScreen.sync.errors.pleaseReconnect'));
      autoDismissSync(); isSyncing.current = false; return;
    }

    setSyncStep('refreshing');
    await delay(400);

    let freshToken: string | null = null;
    try {
      const nowSeconds = Math.floor(Date.now() / 1000);
      const expiresAt  = session?.expires_at ?? 0;
      if (expiresAt - nowSeconds > TOKEN_MARGIN) {
        freshToken = storedAccessToken;
      } else {
        const res = await fetch(`${WS_BASE}/refresh-token`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: storedRefreshToken }),
        });
        if (res.ok) {
          const { access_token, refresh_token: newRefresh, expires_at } = await res.json();
          freshToken = access_token;
          await AsyncStorage.setItem(SESSION_KEY, JSON.stringify({
            ...session, access_token, refresh_token: newRefresh ?? storedRefreshToken, expires_at,
          }));
          accessTokenRef.current = access_token;
        }
      }
    } catch {}

    if (!freshToken) {
      setSyncStep('error', t('messagesScreen.sync.errors.cannotRefresh'));
      autoDismissSync(); isSyncing.current = false; return;
    }
    accessTokenRef.current = freshToken;

    setSyncStep('syncing');
    await delay(300);

    try {
      const uid = userId || session?.user?.id || '';
      if (!uid) {
        setSyncStep('error', t('messagesScreen.sync.errors.userNotFound'));
        autoDismissSync(); isSyncing.current = false; return;
      }

      await loadAllDataWith(uid, freshToken);

      if (activeTab === 'groups') await groupsTabRef.current?.reload();

      setSyncStep('done');
      if (Platform.OS !== 'web') Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      await delay(1400);
      setSyncState({ visible: false, step: 'idle', message: '', error: null });

    } catch {
      setSyncStep('error', t('messagesScreen.sync.errors.syncFailed'));
      autoDismissSync();
    }

    isSyncing.current = false;
  };

  const autoDismissSync = async () => {
    await delay(3000);
    setSyncState({ visible: false, step: 'idle', message: '', error: null });
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const openUserProfile = (targetUserId: string) => {
    if (!targetUserId || targetUserId === userId) return;
    if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedUserId(targetUserId);
    setShowUserProfileModal(true);
  };

  const haptic = () => {
    if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  };

  const q = searchQuery.toLowerCase().trim();

  const filteredConversations = q
    ? conversations.filter(c => {
        const name = c.type === 'private'
          ? `${c.otherUser?.prenom ?? ''} ${c.otherUser?.nom ?? ''}`.toLowerCase()
          : (c.name ?? '').toLowerCase();
        return name.includes(q) || (c.lastMessage?.content ?? '').toLowerCase().includes(q);
      })
    : conversations;

  const filteredOnlineFriends = q
    ? onlineFriends.filter(f => `${f.prenom} ${f.nom}`.toLowerCase().includes(q))
    : onlineFriends;

  const filteredAllFriends = q
    ? allFriends.filter(f => `${f.prenom} ${f.nom}`.toLowerCase().includes(q))
    : allFriends;

  // ── Présence poussée par le backend (v14) ──────────────────────────────────
  // Le backend envoie presence_update { user_id, is_online, expires_in_ms } sur
  // la socket de la liste (après watch_presence). Aucun appel HTTP, aucun
  // minuteur d'interrogation : un seul setTimeout par ami en ligne, qui le
  // fait passer hors ligne au bout de 60 s sans nouveau signal.
  //   true  → en ligne pendant expires_in_ms, puis hors ligne
  //   false → hors ligne tout de suite
  //   null  → statut caché : rien n'est affiché
  const clearPresenceTimer = (id: string) => {
    const timer = presenceTimersRef.current.get(id);
    if (timer) { clearTimeout(timer); presenceTimersRef.current.delete(id); }
  };

  const armPresenceTimer = (id: string, expiresInMs: number) => {
    clearPresenceTimer(id);
    presenceTimersRef.current.set(id, setTimeout(() => {
      presenceTimersRef.current.delete(id);
      applyPresence(id, false);
    }, expiresInMs));
  };

  const applyPresence = (id: string, isOnline: boolean | null, expiresInMs = 0) => {
    clearPresenceTimer(id);
    let value: boolean | null = isOnline;
    if (isOnline === true) {
      if (expiresInMs <= 0) value = false;
      else armPresenceTimer(id, expiresInMs);
    }

    setConversations(prev => prev.map(c =>
      c.type === 'private' && c.otherUser?.id === id
        ? { ...c, otherUser: { ...c.otherUser, isOnline: value } as User }
        : c
    ));
    setAllFriends(prev => prev.map(f => f.id === id ? { ...f, isOnline: value } : f));
    setOnlineFriends(prev => {
      if (value !== true) return prev.filter(f => f.id !== id);
      if (prev.some(f => f.id === id)) return prev;
      const known = friendsCacheRef.current.get(id);
      return known ? [...prev, { ...known, isOnline: true }] : prev;
    });
  };

  // Après une VRAIE lecture HTTP des listes : on mémorise les profils et on
  // arme un minuteur pour chaque ami déjà en ligne (temps restant fourni par
  // le serveur). Un ami déjà expiré est passé hors ligne tout de suite.
  const registerPresence = (users: (User | null | undefined)[]) => {
    users.forEach(u => {
      if (!u) return;
      friendsCacheRef.current.set(u.id, u);
      if (u.isOnline === true) {
        const remaining = u.presenceExpiresInMs ?? 0;
        if (remaining > 0) armPresenceTimer(u.id, remaining);
        else applyPresence(u.id, false);
      } else {
        clearPresenceTimer(u.id);
      }
    });
  };

  // Abonnement (ou mise à jour de l'abonnement) à la présence de tous les amis
  const watchPresence = () => {
    const s = listSocketRef.current;
    if (!s || !s.connected || !userId || !accessTokenRef.current) return;
    s.emit('watch_presence', { user_id: userId, access_token: accessTokenRef.current });
  };

  useEffect(() => { initSession(); }, []);

  // v13 : plus aucune écriture de présence ici (ni à l'ouverture ni à la
  // fermeture de l'écran). La présence est écrite uniquement par le signal
  // du layout racine (route /revue).
  useEffect(() => {
    if (!userId || !accessTokenRef.current) return;
    setupListSocket();
    return () => {
      if (listSocketRef.current) {
        listSocketRef.current.removeAllListeners();
        listSocketRef.current.disconnect();
        listSocketRef.current = null;
      }
      presenceTimersRef.current.forEach(timer => clearTimeout(timer));
      presenceTimersRef.current.clear();
    };
  }, [userId]);

  useEffect(() => { setSearchQuery(''); }, [activeTab]);

  useEffect(() => {
    return () => { setTabBarHidden(false); };
  }, [setTabBarHidden]);

  // ── Ouverture automatique du chat depuis Market ────────────────────────────
  // PATCH V17 : useFocusEffect au lieu de useEffect — voir la note complète
  // en tête de fichier. Se redéclenche à CHAQUE prise de focus de cet écran
  // (et pas seulement quand params.openConversationId change de valeur),
  // ce qui couvre le cas d'un deuxième "Contacter le vendeur" pour le même
  // vendeur (même conversation_id renvoyé par getOrCreateConversation).
  //
  // PATCH V18 : setTabBarHidden(true) différé via setTimeout(0). En
  // arrivant depuis Market, market/_layout.tsx perd le focus au même
  // moment où cet écran le gagne — son cleanup (setParentTabBarHidden
  // (false), qui cible le MÊME Provider racine que le setTabBarHidden
  // utilisé ici) s'exécute en concurrence avec ce useFocusEffect, sans
  // ordre garanti entre deux écrans distincts. Quand le cleanup de
  // Market s'exécutait APRÈS ce setTabBarHidden(true), il l'écrasait —
  // la navbar principale restait visible par-dessus ChatBox (confirmé :
  // recliquer sur Market puis de nouveau sur "Contacter" faisait
  // disparaître la navbar, changement d'ordre en refaisant tourner les
  // deux cycles de focus l'un après l'autre plutôt qu'en même temps).
  // setTimeout(0) repousse cet appel après la fin de la passe synchrone
  // de cleanup/effects de React, pour qu'il s'exécute APRÈS et non plus
  // en course avec celui de market/_layout.tsx.
  useFocusEffect(
    useCallback(() => {
      if (!params.openConversationId || !userId) return;

      setActiveConversation({
        type: 'private',
        id:   params.openConversationId,
        otherUser: {
          id:     params.otherUserId ?? '',
          nom:    params.otherUserNom ?? '',
          prenom: params.otherUserPrenom ?? '',
          avatar_url: null,
        },
        lastMessage: null,
        createdAt:   new Date().toISOString(),
      });
      setViewMode('chat');

      const t = setTimeout(() => setTabBarHidden(true), 0);
      return () => clearTimeout(t);
    }, [params.openConversationId, params.otherUserId, params.otherUserNom, params.otherUserPrenom, userId])
  );

  const initSession = async () => {
    try {
      const raw = await AsyncStorage.getItem(SESSION_KEY);
      if (raw) {
        const s   = JSON.parse(raw);
        const uid = s?.user?.id ?? '';
        const tok = await getValidToken();
        if (uid && tok) {
          accessTokenRef.current = tok;
          const prenom = s?.user?.user_metadata?.prenom ?? s?.user?.prenom ?? '';
          const nom    = s?.user?.user_metadata?.nom    ?? s?.user?.nom    ?? '';
          setUserName(`${prenom} ${nom}`.trim() || t('messagesScreen.defaultUserName'));
          setUserId(uid);
          await loadAllDataWith(uid, tok);
        }
      }
    } catch (e) {
      console.error('[MessagesScreen] initSession:', e);
    } finally {
      setLoading(false);
    }
  };

  const setupListSocket = useCallback(() => {
    if (listSocketRef.current) {
      listSocketRef.current.removeAllListeners();
      listSocketRef.current.disconnect();
    }
    const socket = io(`${WS_BASE}/private-chat`, { transports: ['websocket'], reconnectionDelay: 2000 });
    socket.on('connect', () => {
      console.log('[MessagesScreen] Socket connecté');
      // v14 : un seul événement pour surveiller la présence de tous les amis
      socket.emit('watch_presence', { user_id: userId, access_token: accessTokenRef.current });
    });
    socket.on('disconnect',      () => console.log('[MessagesScreen] Socket déconnecté'));
    // v14 : le backend pousse chaque changement de présence — mise à jour
    // locale des listes, aucune requête HTTP.
    socket.on('presence_update', (data: { user_id: string; is_online: boolean | null; expires_in_ms?: number }) => {
      applyPresence(data.user_id, data.is_online, data.expires_in_ms ?? 0);
    });
    listSocketRef.current = socket;
  }, [userId]);

  const loadAllDataWith = async (uid: string, tok: string) => {
    await Promise.all([
      api('getConversations', uid).then(d => { if (d.success) { setConversations(d.conversations ?? []); registerPresence((d.conversations ?? []).map((c: Conversation) => c.otherUser)); } }).catch(() => {}),
      api('getFriendsOnline', uid).then(d => { if (d.success) { setOnlineFriends(d.friends ?? []); registerPresence(d.friends ?? []); } }).catch(() => {}),
      api('getAllFriends',    uid).then(d => { if (d.success) { setAllFriends(d.friends ?? []); registerPresence(d.friends ?? []); } }).catch(() => {}),
      fetch(`${WS_BASE}/profile`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getProfile', user_id: uid, access_token: tok }),
      })
        .then(r => r.json())
        .then(d => { if (d.success && d.profile?.role) setUserRole(d.profile.role); })
        .catch(() => {}),
    ]);
    // v14 : met à jour l'abonnement de présence (liste d'amis, jeton rafraîchi)
    watchPresence();
  };

  const loadAllData = useCallback(async () => {
    if (!userId) return;
    await Promise.all([loadConversations(), loadOnlineFriends(), loadAllFriends()]);
    // v14 : la liste d'amis a pu changer → on met l'abonnement de présence à jour
    watchPresence();
    groupsTabRef.current?.reload();
  }, [userId]);

  const loadConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const d = await api('getConversations', userId);
      if (d.success) {
        setConversations(d.conversations ?? []);
        registerPresence((d.conversations ?? []).map((c: Conversation) => c.otherUser));
      }
    } catch {}
  }, [userId]);

  const loadOnlineFriends = useCallback(async () => {
    if (!userId) return;
    try {
      const d = await api('getFriendsOnline', userId);
      if (d.success) { setOnlineFriends(d.friends ?? []); registerPresence(d.friends ?? []); }
    } catch {}
  }, [userId]);

  const loadAllFriends = useCallback(async () => {
    if (!userId) return;
    try {
      const d = await api('getAllFriends', userId);
      if (d.success) { setAllFriends(d.friends ?? []); registerPresence(d.friends ?? []); }
    } catch {}
  }, [userId]);

  const onRefresh = async () => {
    haptic(); setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  // ── Callback de mise à jour du cache (v12) ────────────────────────────────
  // Transmis à ChatBox via onMessagesUpdate. Appelé à chaque changement du
  // state messages/hasMore de ChatBox — tient le cache à jour en continu,
  // pas seulement au démontage (qui n'arrive plus systématiquement).
  const handleMessagesUpdate = useCallback((conversationId: string, messages: Message[], hasMore: boolean) => {
    setCached(conversationId, messages, hasMore);
  }, [setCached]);

  const openGroupChat = (group: GroupItem) => {
    haptic();
    setActiveConversation({
      type: 'group', id: group.id, name: group.name,
      description: group.description ?? undefined,
      memberCount: group.member_count, createdAt: group.created_at,
      lastMessage: group.last_message ? {
        content:    group.last_message.content,
        createdAt:  group.last_message.created_at,
        isFromMe:   group.last_message.is_from_me,
        senderName: group.last_message.is_from_me ? undefined : group.last_message.sender_name,
      } : null,
    });
    setViewMode('chat'); setTabBarHidden(true);
  };

  const openPrivateChat = async (friend: User) => {
    haptic();
    try {
      const d = await api('getOrCreateConversation', userId, { friend_id: friend.id });
      if (d.success) {
        setActiveConversation({ type: 'private', id: d.conversation_id, otherUser: friend, lastMessage: null, createdAt: new Date().toISOString() });
        setViewMode('chat'); setTabBarHidden(true);
      }
    } catch {}
  };

  const openConversation = (conv: Conversation) => {
    haptic(); setActiveConversation(conv); setViewMode('chat'); setTabBarHidden(true);
  };

  // ── backToList (v12) ───────────────────────────────────────────────────────
  // CORRECTIF : ne remet PLUS activeConversation à null. On repasse juste en
  // 'tabs' — ChatBox reste monté (caché via style plus bas dans le rendu),
  // donc son socket et son state (messages, scroll, saisie en cours) restent
  // intacts. Revenir sur la même conversation ensuite ne redéclenche donc
  // aucun nouveau join_conversation ni rechargement DB.
  const backToList = () => {
    haptic(); setViewMode('tabs'); setTabBarHidden(false); loadConversations();
  };

  const formatTime = (d: string) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const m   = Math.floor((now.getTime() - date.getTime()) / 60000);
    if (m < 1)  return t('messagesScreen.time.now');
    if (m < 60) return t('messagesScreen.time.minutes', { count: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t('messagesScreen.time.hours', { count: h });
    const day = Math.floor(h / 24);
    if (day < 7) return t('messagesScreen.time.days', { count: day });
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  const renderAvatar = (user: User | undefined, size = 50) => {
    if (!user) return null;
    return (
      <TouchableOpacity
        onPress={(e) => {
          // PATCH V28 — Cet avatar est imbriqué dans le TouchableOpacity
          // de la carte de conversation (openConversation). Sans stopper
          // la propagation, les deux gestionnaires tactiles entrent en
          // conflit : le premier clic est parfois absorbé par la carte
          // parente (qui ouvre le chat au lieu du profil), et il fallait
          // revenir en arrière puis recliquer pour que openUserProfile
          // se déclenche enfin. e.stopPropagation() garantit qu'un tap
          // sur l'avatar ne déclenche QUE l'ouverture du profil, jamais
          // la navigation de la carte parente, dès le premier clic.
          e.stopPropagation();
          openUserProfile(user.id);
        }}
        activeOpacity={0.75}
        style={[S.avatar, { width: size, height: size, borderRadius: size / 2 }]}
      >
        <Text style={[S.avatarText, { fontSize: size / 2.5 }]}>
          {user.prenom.charAt(0)}{user.nom.charAt(0)}
        </Text>
        {/* v13 : point vert uniquement si isOnline === true (false et null : rien) */}
        {user.isOnline === true && (
          <View style={[S.onlineIndicator, { width: size / 4, height: size / 4, borderRadius: size / 8, bottom: size / 20, right: size / 20 }]} />
        )}
      </TouchableOpacity>
    );
  };

  const totalUnread = conversations.reduce((s, c) => s + (c.unreadCount || 0), 0);

  const syncSpinDeg = syncIconSpin.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });

  // ── Rendu ChatBox (v12) ─────────────────────────────────────────────────────
  // Reste monté tant qu'activeConversation existe, MÊME si viewMode revient à
  // 'tabs' — seul son style change (display: 'none') pour le masquer sans le
  // démonter. Ceci préserve le socket et le state interne entre deux visites
  // de la même conversation. Ouvrir une conversation DIFFÉRENTE remplace
  // activeConversation, ce qui démonte l'ancien ChatBox (changement de `key`)
  // et en monte un nouveau — le cache de messages, lui, survit dans
  // useConversationsCache et réhydrate instantanément via initialMessages.
  const chatVisible = viewMode === 'chat' && !!activeConversation && !!accessTokenRef.current;

  return (
    <View style={S.container}>

      {activeConversation && accessTokenRef.current && (
        <View style={[StyleSheet.absoluteFill, !chatVisible && S.hiddenChat]}>
          <ChatBox
            key={activeConversation.id}
            conversationId={activeConversation.id}
            conversationType={activeConversation.type}
            userId={userId}
            userName={userName}
            userRole={userRole}
            accessToken={accessTokenRef.current}
            otherUser={activeConversation.otherUser}
            groupName={activeConversation.name}
            memberCount={activeConversation.memberCount}
            initialMessages={getCached(activeConversation.id)?.messages}
            initialHasMore={getCached(activeConversation.id)?.hasMore}
            onMessagesUpdate={handleMessagesUpdate}
            onBack={backToList}
            onNewMessage={loadConversations}
            onProfilePress={openUserProfile}
          />
          {/* PATCH V29 — UserProfileView doit aussi pouvoir s'afficher ici.
              Avant ce correctif, ce composant n'existait que dans la branche
              liste (plus bas dans ce fichier) : cliquer sur la flèche > du
              header de ChatBox appelait bien openUserProfile (setSelectedUserId
              + setShowUserProfileModal(true)), mais comme cette branche
              retournait uniquement <ChatBox/>, rien ne s'affichait — il
              fallait d'abord faire "retour" pour atteindre la branche liste
              où UserProfileView est monté, PUIS recliquer sur l'avatar pour
              enfin voir le profil. Dupliquer UserProfileView ici règle le
              problème : le modal s'ouvre dès le premier clic sur la flèche >,
              sans quitter la conversation. */}
          {showUserProfileModal && selectedUserId && (
            <UserProfileView
              userId={selectedUserId}
              viewerId={userId}
              accessToken={accessTokenRef.current}
              asModal
              onClose={() => { setShowUserProfileModal(false); setSelectedUserId(null); }}
            />
          )}
        </View>
      )}

      {!chatVisible && (
        <View style={S.container}>

          <SyncOverlay state={syncState} t={t} />

          {/* PATCH V23 — Recherche et onglets regroupés dans UN SEUL bloc blanc
              (topPanel), collé tout en haut de l'écran. Plus de séparation
              visuelle entre la barre de recherche et Historique/En ligne/
              Amis/Groupes : les deux font partie du même panneau. */}
          <View style={S.topPanel}>
            <View style={S.headerBlock}>
              {totalUnread > 0 && <View style={S.unreadDot} />}

              <TouchableOpacity
                activeOpacity={1}
                style={[S.searchField, isSearchFocused && S.searchFieldFocused]}
                onPress={() => searchInputRef.current?.focus()}
              >
                <Ionicons name="search" size={14} color="#999" style={{ marginRight: 6 }} />
                <TextInput
                  ref={searchInputRef}
                  style={S.searchInput}
                  placeholder={
                    activeTab === 'history' ? t('messagesScreen.search.placeholderHistory')
                    : activeTab === 'online' ? t('messagesScreen.search.placeholderOnline')
                    : activeTab === 'friends' ? t('messagesScreen.search.placeholderFriends')
                    : t('messagesScreen.search.placeholderGroups')
                  }
                  placeholderTextColor="#AAA"
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  clearButtonMode="while-editing"
                  underlineColorAndroid="transparent"
                  selectionColor="#8A2BE2"
                />
                {searchQuery.length > 0 && Platform.OS !== 'ios' && (
                  <TouchableOpacity onPress={() => setSearchQuery('')} hitSlop={10}>
                    <Ionicons name="close-circle" size={14} color="#AAA" />
                  </TouchableOpacity>
                )}
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => { haptic(); handleSync(); }}
                style={S.syncBtn}
                activeOpacity={0.7}
                hitSlop={6}
              >
                <Animated.View style={{ transform: [{ rotate: syncSpinDeg }] }}>
                  <Ionicons name="refresh" size={16} color="#8A2BE2" />
                </Animated.View>
              </TouchableOpacity>
            </View>

            <View style={S.tabs}>
              {([
                { key: 'history', icon: 'time-outline',          label: t('messagesScreen.tabs.history') },
                { key: 'online',  icon: 'radio-button-on',       label: t('messagesScreen.tabs.online')  },
                { key: 'friends', icon: 'people-outline',        label: t('messagesScreen.tabs.friends') },
                { key: 'groups',  icon: 'people-circle-outline', label: t('messagesScreen.tabs.groups')  },
              ] as const).map(t2 => (
                <TouchableOpacity
                  key={t2.key}
                  style={[S.tab, activeTab === t2.key && S.tabActive]}
                  onPress={() => { haptic(); setActiveTab(t2.key); }}
                  activeOpacity={0.7}
                >
                  <Ionicons name={t2.icon as any} size={20} color={activeTab === t2.key ? '#8A2BE2' : '#999'} />
                  <Text style={[S.tabText, activeTab === t2.key && S.tabTextActive]}>{t2.label}</Text>
                  {t2.key === 'history' && totalUnread > 0 && (
                    <View style={S.tabBadge}><Text style={S.tabBadgeTxt}>{totalUnread}</Text></View>
                  )}
                  {t2.key === 'online' && onlineFriends.length > 0 && (
                    <View style={[S.tabBadge, { backgroundColor: '#10B981' }]}>
                      <Text style={S.tabBadgeTxt}>{onlineFriends.length}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <ScrollView
            style={{ flex: 1 }}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#8A2BE2" />}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            onTouchEnd={handleDoubleTap}
          >

            {activeTab === 'history' && (
              <View style={S.section}>
                {loading ? <LoadingText t={t} />
                : filteredConversations.length === 0
                  ? q
                    ? <EmptyState icon="search-outline" title={t('messagesScreen.empty.noResultsTitle')} subtitle={t('messagesScreen.empty.noConversationsFor', { query: searchQuery })} />
                    : <EmptyState icon="chatbubbles-outline" title={t('messagesScreen.empty.noMessagesTitle')} subtitle={t('messagesScreen.empty.noMessagesSubtitle')} />
                : filteredConversations.map(conv => (
                  <TouchableOpacity key={conv.id} style={S.item} onPress={() => openConversation(conv)} activeOpacity={0.7}>
                    {conv.type === 'private'
                      ? renderAvatar(conv.otherUser)
                      : <View style={[S.avatar, S.groupAvatar]}><Ionicons name="people" size={24} color="#fff" /></View>
                    }
                    <View style={S.itemBody}>
                      <View style={S.itemRow}>
                        <Text style={S.itemName} numberOfLines={1}>
                          {conv.type === 'private' ? `${conv.otherUser?.prenom} ${conv.otherUser?.nom}` : conv.name}
                        </Text>
                        <Text style={S.itemTime}>{conv.lastMessage ? formatTime(conv.lastMessage.createdAt) : ''}</Text>
                      </View>
                      <View style={S.itemRow}>
                        <Text style={S.itemLast} numberOfLines={1}>
                          {conv.lastMessage?.isFromMe && `${t('messagesScreen.you')} `}
                          {conv.lastMessage?.senderName && `${conv.lastMessage.senderName} : `}
                          {getLastMessagePreview(conv.lastMessage, t)}
                        </Text>
                        {(conv.unreadCount || 0) > 0 && (
                          <View style={S.unreadBadge}><Text style={S.unreadBadgeTxt}>{conv.unreadCount}</Text></View>
                        )}
                      </View>
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {activeTab === 'online' && (
              <View style={S.section}>
                {loading ? <LoadingText t={t} />
                : filteredOnlineFriends.length === 0
                  ? q
                    ? <EmptyState icon="search-outline" title={t('messagesScreen.empty.noResultsTitle')} subtitle={t('messagesScreen.empty.noOnlineFriendsFor', { query: searchQuery })} />
                    : <EmptyState icon="wifi-outline" title={t('messagesScreen.empty.noOnlineFriendsTitle')} />
                : filteredOnlineFriends.map(f => (
                  <TouchableOpacity key={f.id} style={S.item} onPress={() => openPrivateChat(f)} activeOpacity={0.7}>
                    {renderAvatar({ ...f, isOnline: true })}
                    <View style={S.itemBody}>
                      <Text style={S.itemName}>{f.prenom} {f.nom}</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <View style={S.onlineDot} />
                        <Text style={{ fontSize: 12, color: '#10B981', fontWeight: '600' }}>{t('messagesScreen.status.online')}</Text>
                      </View>
                    </View>
                    <Ionicons name="chatbubble-outline" size={22} color="#8A2BE2" />
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {activeTab === 'friends' && (
              <View style={S.section}>
                {loading ? <LoadingText t={t} />
                : filteredAllFriends.length === 0
                  ? q
                    ? <EmptyState icon="search-outline" title={t('messagesScreen.empty.noResultsTitle')} subtitle={t('messagesScreen.empty.noFriendsFor', { query: searchQuery })} />
                    : <EmptyState icon="people-outline" title={t('messagesScreen.empty.noFriendsTitle')} />
                : filteredAllFriends.map(f => (
                  <TouchableOpacity key={f.id} style={S.item} onPress={() => openPrivateChat(f)} activeOpacity={0.7}>
                    {renderAvatar(f)}
                    <View style={S.itemBody}>
                      <Text style={S.itemName}>{f.prenom} {f.nom}</Text>
                      {/* v13 : statut affiché uniquement si true ou false.
                          null (statut caché) → aucun texte. */}
                      {(f.isOnline === true || f.isOnline === false) && (
                        <Text style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                          {f.isOnline ? t('messagesScreen.status.online') : t('messagesScreen.status.offline')}
                        </Text>
                      )}
                    </View>
                    <Ionicons name="chatbubble-outline" size={22} color="#8A2BE2" />
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {activeTab === 'groups' && (
              <GroupsTab
                ref={groupsTabRef}
                userId={userId}
                searchQuery={searchQuery}
                onOpenGroupChat={openGroupChat}
                userRole={userRole}
              />
            )}

          </ScrollView>

          {showUserProfileModal && selectedUserId && (
            <UserProfileView
              userId={selectedUserId}
              viewerId={userId}
              accessToken={accessTokenRef.current}
              asModal
              onClose={() => { setShowUserProfileModal(false); setSelectedUserId(null); }}
            />
          )}
        </View>
      )}
    </View>
  );
}

const SO = StyleSheet.create({
  backdrop:        { flex: 1, backgroundColor: 'rgba(10,0,30,0.55)', justifyContent: 'center', alignItems: 'center', paddingHorizontal: 32 },
  card:            { width: '100%', backgroundColor: '#fff', borderRadius: 24, paddingVertical: 32, paddingHorizontal: 28, alignItems: 'center', shadowColor: '#4B0082', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.18, shadowRadius: 24, elevation: 16 },
  iconWrap:        { marginBottom: 18 },
  iconCircle:      { width: 72, height: 72, borderRadius: 36, backgroundColor: '#F0E8FF', justifyContent: 'center', alignItems: 'center' },
  iconCircleDone:  { backgroundColor: '#DCFCE7' },
  iconCircleError: { backgroundColor: '#FEE2E2' },
  title:           { fontSize: 18, fontWeight: '700', color: '#1A0033', marginBottom: 6, textAlign: 'center' },
  message:         { fontSize: 13, color: '#6B21A8', textAlign: 'center', marginBottom: 22, lineHeight: 19 },
  steps:           { width: '100%', gap: 0 },
  stepRow:         { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  stepDot:         { width: 24, height: 24, borderRadius: 12, justifyContent: 'center', alignItems: 'center', marginRight: 10, flexShrink: 0 },
  stepDotDone:     { backgroundColor: '#10B981' },
  stepDotActive:   { backgroundColor: '#8A2BE2' },
  stepDotPending:  { backgroundColor: '#E5E7EB' },
  stepDotTxt:      { fontSize: 11, color: '#fff', fontWeight: '700' },
  stepLabel:       { fontSize: 13, flex: 1 },
  stepLabelActive: { color: '#8A2BE2', fontWeight: '700' },
  stepLabelDone:   { color: '#10B981', fontWeight: '600' },
  stepLabelPending:{ color: '#9CA3AF' },
  errorBox:        { flexDirection: 'row', alignItems: 'flex-start', backgroundColor: '#FEF2F2', borderRadius: 10, padding: 12, marginTop: 4, width: '100%' },
  errorBoxTxt:     { fontSize: 13, color: '#DC2626', flex: 1, lineHeight: 18 },
});

const S = StyleSheet.create({
  container:      { flex: 1, backgroundColor: '#F5F5F5' },

  // v12 — masque ChatBox sans le démonter (voir chatVisible plus haut)
  hiddenChat: {
    display: 'none',
  },

  // PATCH V27 — paddingTop augmenté (12/8 → 24/16) : toujours coupé par
  // l'encoche/status bar au précédent réglage.
  topPanel: {
    backgroundColor: '#fff',
    paddingTop: Platform.OS === 'ios' ? 24 : 16,
  },
  headerBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingBottom: 10,
  },
  unreadDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: '#FF3B7F',
  },
  // PATCH V30 — Zone tactile élargie : hauteur 38→44 et padding vertical
  // ajouté sur searchInput (au lieu de 0) pour que le tap réagisse même
  // en touchant légèrement au-dessus/en-dessous du texte, pas uniquement
  // sur la ligne de base exacte du curseur.
  searchField: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: 44,
    backgroundColor: '#F5F3FA',
    borderRadius: 12,
    paddingHorizontal: 12,
    borderWidth: 1.5,
    borderColor: 'transparent',
  },
  searchFieldFocused: {
    borderColor: '#C4A6F0',
    backgroundColor: '#fff',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    paddingVertical: 8,
    paddingHorizontal: 0,
    // PATCH V23 : sur web, un <input> affiche par défaut un contour noir
    // natif au focus ("outline"). outlineWidth: 0 le neutralise (RNW
    // transmet cette clé de style directement au CSS de l'input HTML) ;
    // le style moderne de focus est géré à la place par searchFieldFocused
    // (bordure violette douce sur le conteneur).
    ...(Platform.OS === 'web' ? { outlineWidth: 0, outlineStyle: 'none' as any } : {}),
  },
  syncBtn: {
    width: 38,
    height: 38,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F3E8FF',
  },

  tabs:           { flexDirection: 'row' },
  tab:            { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 12, gap: 4, position: 'relative' },
  tabActive:      { borderBottomWidth: 3, borderBottomColor: '#8A2BE2' },
  tabText:        { fontSize: 11, color: '#999', fontWeight: '500' },
  tabTextActive:  { color: '#8A2BE2', fontWeight: 'bold' },
  tabBadge:       { position: 'absolute', top: 6, right: 4, backgroundColor: '#FF0080', borderRadius: 10, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4 },
  tabBadgeTxt:    { color: '#fff', fontSize: 10, fontWeight: 'bold' },
  section:        { paddingVertical: 8 },
  item:           { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', padding: 15, marginHorizontal: 10, marginVertical: 5, borderRadius: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 4, elevation: 2 },
  itemBody:       { flex: 1, marginLeft: 12 },
  itemRow:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  itemName:       { fontSize: 15, fontWeight: '600', color: '#333', flex: 1 },
  itemTime:       { fontSize: 12, color: '#999', marginLeft: 8, flexShrink: 0 },
  itemLast:       { fontSize: 14, color: '#666', flex: 1 },
  unreadBadge:    { backgroundColor: '#FF0080', borderRadius: 12, minWidth: 24, height: 24, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 8 },
  unreadBadgeTxt: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  avatar:         { width: 50, height: 50, borderRadius: 25, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center', position: 'relative' },
  groupAvatar:    { backgroundColor: '#FF8C00' },
  avatarText:     { color: '#fff', fontWeight: 'bold' },
  onlineIndicator:{ position: 'absolute', backgroundColor: '#10B981', borderWidth: 2, borderColor: '#fff' },
  onlineDot:      { width: 8, height: 8, borderRadius: 4, backgroundColor: '#10B981' },
});
